import { SectionPreviewPane } from '@/components/common/section-preview-pane';
import { ZyPreviewButton } from '@/components/common/zypreview-button';
import { AppPageLayout } from '@/components/layout/app-page-layout';
import { GlobalTopBar } from '@/components/layout/global-top-bar';
import { TiptapEditor, type TiptapOnCreateCallbackProps } from '@/components/tiptap-editor/tiptap-editor';
import { migrateZybookMathStrings } from '@/components/tiptap-editor/tiptap-extension/math-extension';
import type { ZyDocumentAttributes } from '@/components/tiptap-editor/tiptap-extension/zydocument-extension';
import { ZyDocumentTitle } from '@/components/tiptap-editor/tiptap-ui/zy-document-title/zy-document-title';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useZySectionLivePreview } from '@/hooks/use-zy-section-live-preview';
import { getMaImagesLibraryBaseUrl } from '@/lib/ma-images-library';
import { zyAuthor } from '@/lib/servers';
import { renderJsonTo, setDocAttributes } from '@/lib/zy-tiptap-utils';
import { getMaDocument, getTiptapDocumentJson, getZybook, getZybookSubject } from '@/lib/zybooks-api';
import { convertZyXml, convertZybookTestQuestionsToProsemirror, parseZybookTestQuestionInput } from '@/lib/zybooks-parser';
import { linearalgebra11JSON } from '@/tests/demo-contents';
import { Editor as MonacoEditor } from '@monaco-editor/react';
import { Editor, type JSONContent } from '@tiptap/react';
import { Copy, FileJsonIcon, LoaderCircleIcon, RefreshCwIcon } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { toast } from 'sonner';

const editorOptions = {
    wordWrap: 'on' as any,
};

const EditorDev: React.FC = () => {
    const defaultDemoContent = linearalgebra11JSON;

    const [outputType, setOutputType] = useState<'markdown' | 'html' | 'zyxml' | 'json'>('json');
    const [jsonContent, setJsonContent] = useState<Record<string, any>>();
    const [htmlContent, setHtmlContent] = useState<string>('');
    const [editor, setEditor] = useState<Editor | null>(null);

    const [showImportModal, setShowImportModal] = useState<boolean>(false);
    const [importText, setImportText] = useState<string>('');
    const [importDevBookName, setImportDevBookName] = useState<string>('LinearAlgebraDev');
    const [importSubject, setImportSubject] = useState<string>('LinearAlgebra');
    const [importReleaseId, setImportReleaseId] = useState<string>('65');
    const [importSectionId, setImportSectionId] = useState<string>('2a826cdc-1e15-17d4-66c3-271c3a926fb5');
    const [importMaDocumentId, setImportMaDocumentId] = useState<string>('');
    const [linkedTestQuestionDocumentId, setLinkedTestQuestionDocumentId] = useState<string>('');
    const [importServerDevBookName, setImportServerDevBookName] = useState<string>('LinearAlgebraDev');

    const [isImportingContent, setIsImportingContent] = useState<boolean>(false);
    const [importModalError, setImportModalError] = useState<string | null>(null);
    const [pmJsonUseMonaco, setPMJsonUseMonaco] = useState<boolean>(false);
    const [outputUseMonaco, setOutputUseMonaco] = useState<boolean>(false);
    const [isDevMode, setIsDevMode] = useState<boolean>(true);
    const [isLivePMJsonEnabled, setIsLivePMJsonEnabled] = useState<boolean>(false);
    const [isLiveOutputEnabled, setIsLiveOutputEnabled] = useState<boolean>(false);
    const [isInitialContentLoaded, setIsInitialContentLoaded] = useState(false);

    const handleAfterConvert = useCallback((convertedJson: JSONContent, currentEditor: Editor) => {
        setHtmlContent(renderJsonTo(convertedJson, currentEditor.extensionManager.extensions, 'html'));
    }, []);

    const parsedLinkedTestQuestionDocumentId = useMemo(() => {
        const value = Number(linkedTestQuestionDocumentId.trim());
        return Number.isInteger(value) && value > 0 ? value : null;
    }, [linkedTestQuestionDocumentId]);

    const {
        iframeRef,
        iframeURL,
        connectionError,
        tiptapEditorRef,
        previewMode,
        previewError,
        isRenderingOutput,
        zybooksJsonContent,
        togglePreviewMode,
        refreshOutputs,
        updateOutputs,
    } = useZySectionLivePreview(editor, {
        onAfterConvert: handleAfterConvert,
        linkedTestQuestionDocumentIds: parsedLinkedTestQuestionDocumentId != null ? [parsedLinkedTestQuestionDocumentId] : [],
    });

    const setDemoContent = (editor: Editor) => {
        setDocAttributes(editor, defaultDemoContent.attrs || {});

        // setContent deferred to avoid blocking initial render
        requestAnimationFrame(() => {
            const scheduleSetContent = () => {
                editor.commands.setContentWithMeta(defaultDemoContent);
                setIsInitialContentLoaded(true);
                editor.commands.focus('start');
            };

            if (typeof requestIdleCallback !== 'undefined') {
                requestIdleCallback(scheduleSetContent, { timeout: 100 });
            } else {
                setTimeout(scheduleSetContent, 0);
            }
        });
    };

    const handleTipTapCreate = ({ editor }: TiptapOnCreateCallbackProps) => {
        setDemoContent(editor);
        setEditor(editor);
    };

    const handleTiptapUpdate = (editor: Editor) => {
        if (!editor) {
            console.warn('Editor is not initialized yet.');
            return;
        }

        if (isLivePMJsonEnabled) {
            const jsonContent = editor.getJSON();
            setJsonContent(jsonContent);
        }

        if (isLiveOutputEnabled) {
            updateOutputs(editor);
        }
    };

    const resetImportDocAttributes = () => {
        if (!editor) {
            console.warn('Editor is not initialized yet.');
            return;
        }
        setDocAttributes(editor, {
            subjectName: null,
            releaseId: null,
            baseImageUrl: null,
            sectionId: null,
        });
    };

    /**
     * Clears doc attrs used for import, applies optional attrs from the converted document,
     * then applies subject / release / section from the import modal.
     */
    const setImportDocAttribute = (
        attributes?: Partial<ZyDocumentAttributes>,
        context?: { subject?: string; releaseId?: string; sectionId?: string },
    ) => {
        if (!editor) {
            console.warn('Editor is not initialized yet.');
            return;
        }

        resetImportDocAttributes();

        const docAttrs: Partial<ZyDocumentAttributes> = { ...attributes };
        const subject = (context?.subject ?? importSubject).trim();
        const releaseId = parseInt((context?.releaseId ?? importReleaseId).trim());
        const sectionId = (context?.sectionId ?? importSectionId).trim();

        if (subject) docAttrs.subjectName = subject;
        if (releaseId) docAttrs.releaseId = releaseId;
        if (sectionId) docAttrs.sectionId = sectionId;
        if (subject && releaseId != null) {
            // // docAttrs.baseImageUrl = `https://zytools.zybooks.com/zyAuthor/${subject}/${releaseId}/IMAGES/`;
            // docAttrs.baseImageUrl = 'https://zytools.zybooks.com/zyAuthor/MaImages/Library/';
            docAttrs.baseImageUrl = getMaImagesLibraryBaseUrl();
        }

        setDocAttributes(editor, docAttrs);
    };

    const handleImportZyxml = async () => {
        if (!importText.trim()) {
            alert('Please paste some markdown content to import.');
            return;
        }

        if (!editor) {
            console.error('Editor is not initialized yet.');
            return;
        }

        try {
            setIsImportingContent(true);
            const converted = (await convertZyXml(importText.trim(), 'json', {
                devBookName: importDevBookName.trim(),
                subject: importSubject.trim(),
                releaseId: importReleaseId.trim(),
            })) as { section: any; output: JSONContent };
            if (!converted) {
                alert('Failed to convert zyXML content.');
                setIsImportingContent(false);
                return;
            }
            editor.commands.setContentWithMeta(converted.output);
            migrateZybookMathStrings(editor);
            setImportDocAttribute(converted.output.attrs);
            setShowImportModal(false);
            setIsImportingContent(false);
        } catch (error) {
            console.error('Error importing zyXML:', error);
            setIsImportingContent(false);
        }
    };

    const handleImport = async (type: 'html' | 'json') => {
        if (!importText.trim()) {
            alert('Please paste some markdown content to import.');
            return;
        }
        if (!editor) {
            console.warn('Editor is not initialized yet.');
            return;
        }

        setIsImportingContent(true);
        let jsonContent: JSONContent = {};
        let docAttrs: Partial<ZyDocumentAttributes> = {};
        if (type === 'json') {
            try {
                jsonContent = JSON.parse(importText);
                docAttrs = jsonContent.attrs || {};
            } catch {
                alert('Invalid JSON content.');
                setIsImportingContent(false);
                return;
            }
        }

        // setContent in next tick so it's not blocking
        setTimeout(() => {
            editor.commands.setContentWithMeta(type === 'json' ? jsonContent : importText);
            migrateZybookMathStrings(editor);
            setImportDocAttribute(docAttrs);
            setIsImportingContent(false);
            setShowImportModal(false);
        }, 0);
    };

    const handleImportTestBank = () => {
        if (!importText.trim()) {
            setImportModalError('Please paste test bank JSON to import.');
            return;
        }
        if (!editor) {
            setImportModalError('Editor is not initialized yet.');
            return;
        }

        setImportModalError(null);
        setIsImportingContent(true);

        try {
            const parsed = JSON.parse(importText);
            const questions = parseZybookTestQuestionInput(parsed);
            const subject = importSubject.trim();
            const releaseId = importReleaseId.trim();
            const converted = convertZybookTestQuestionsToProsemirror(questions);
            setDocAttributes(editor, {
                subjectName: subject,
                releaseId,
                sectionId: importSectionId.trim(),
                documentType: 'test_question',
                baseImageUrl: null,
            });
            setTimeout(() => {
                editor.commands.setContentWithMeta(converted);
                migrateZybookMathStrings(editor);
                setIsImportingContent(false);
                setShowImportModal(false);
            }, 0);
        } catch (error) {
            console.error('Error importing test bank:', error);
            setImportModalError(error instanceof Error ? error.message : 'Failed to import test bank');
            setIsImportingContent(false);
        }
    };

    const handleImportMaDocument = async () => {
        const devBookName = importServerDevBookName.trim();
        if (!devBookName) {
            setImportModalError('Enter a devbook code (e.g. LinearAlgebraDev).');
            return;
        }

        const idStr = importMaDocumentId.trim();
        const maDocumentId = Number(idStr);
        if (!idStr || !Number.isInteger(maDocumentId) || maDocumentId <= 0) {
            setImportModalError('Enter a positive integer ma_document_id.');
            return;
        }
        if (!editor) {
            setImportModalError('Editor is not initialized yet.');
            return;
        }

        setImportModalError(null);
        setIsImportingContent(true);
        try {
            const [maDocument, zybook] = await Promise.all([getMaDocument(maDocumentId), getZybook(devBookName)]);
            const tiptapJson = await getTiptapDocumentJson(maDocument.document_name);
            const zybookSubject = await getZybookSubject(zybook.zybook_id);
            const subjectName = zybookSubject.content_source;
            const isLabDevBook = zybookSubject.lab_dev_book === importServerDevBookName;

            const devbookName = isLabDevBook ? zybookSubject.dev_book : devBookName;
            const devbookInfo = await zyAuthor.get(`modern_authoring/get_devbook_info?devBookName=${encodeURIComponent(devbookName)}`);
            const releaseId = devbookInfo.release_number != null ? String(devbookInfo.release_number) : '';

            setImportSubject(subjectName);
            setImportDevBookName(devBookName);
            setImportReleaseId(releaseId);
            setImportSectionId(maDocument.section_guid);

            const maZybookDocument = maDocument.ma_zybook_documents.find(mzd => mzd.zybook_id === zybook.zybook_id);
            if (!maZybookDocument) {
                setImportModalError('No ma_zybook_document found for the given zybook_id.');
                return;
            }

            const docAttributes: Partial<ZyDocumentAttributes> = {
                ...tiptapJson.attrs,
                subjectName,
                releaseId,
                sectionId: maDocument.section_guid,
                title: {
                    default: maZybookDocument.section_title,
                },
                chapterNumber: maZybookDocument.chapter_number,
                sectionNumber: maZybookDocument.section_number,
            };

            setTimeout(() => {
                editor.commands.setContentWithMeta(tiptapJson);
                migrateZybookMathStrings(editor);
                setDocAttributes(editor, docAttributes);
                setIsImportingContent(false);
                setShowImportModal(false);
            }, 0);
        } catch (error) {
            console.error('Error importing MA document:', error);
            setImportModalError(error instanceof Error ? error.message : 'Failed to import document');
        } finally {
            setIsImportingContent(false);
        }
    };

    const resetEditor = () => {
        if (!editor) {
            console.warn('Editor is not initialized yet.');
            return;
        }
        setDemoContent(editor);
    };

    const handleCopyOutput = () => {
        let contentToCopy = '';
        if (outputType === 'html') {
            contentToCopy = htmlContent;
        } else if (outputType === 'json') {
            contentToCopy = JSON.stringify(zybooksJsonContent ?? {}, null, 2);
        }
        navigator.clipboard.writeText(contentToCopy);
    };

    const handleRefreshPMJson = () => {
        if (!editor) {
            console.warn('Editor is not initialized yet.');
            return;
        }
        const jsonContent = editor.getJSON();
        setJsonContent(jsonContent);
    };

    const handleRefreshOutput = async () => {
        try {
            await refreshOutputs();
        } catch (error) {
            toast.error('Error refreshing output', {
                description: (error as Error).message,
            });
        }
    };

    const renderedOutput = useMemo(() => {
        switch (outputType) {
            case 'html':
                return { language: 'html', value: htmlContent || '' };
            case 'json':
                return { language: 'json', value: JSON.stringify(zybooksJsonContent ?? {}, null, 2) || '' };
            default:
                return { language: 'markdown', value: '' };
        }
    }, [outputType, htmlContent, zybooksJsonContent]);

    return (
        <AppPageLayout className="flex h-[calc(100svh-49px)] flex-col overflow-hidden">
            <GlobalTopBar.Start>
                {editor && (
                    <div className="min-w-0">
                        <ZyDocumentTitle editor={editor} />
                    </div>
                )}
            </GlobalTopBar.Start>
            <GlobalTopBar.Actions>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Dev mode</span>
                    <Switch
                        checked={isDevMode}
                        onCheckedChange={setIsDevMode}
                    />
                </div>
                {isDevMode && (
                    <label className="flex items-center gap-2 text-xs text-gray-600">
                        <span className="whitespace-nowrap">Linked TQ doc id</span>
                        <Input
                            className="h-8 w-28"
                            inputMode="numeric"
                            placeholder="optional"
                            value={linkedTestQuestionDocumentId}
                            onChange={e => setLinkedTestQuestionDocumentId(e.target.value)}
                        />
                    </label>
                )}
                <ZyPreviewButton
                    editor={editor}
                    previewMode={previewMode}
                    disabled={isRenderingOutput}
                    onTogglePreview={togglePreviewMode}
                />
                <Button
                    size="sm"
                    onClick={() => resetEditor()}
                >
                    Reset Editor
                </Button>
                <Button
                    size="sm"
                    onClick={() => setShowImportModal(true)}
                >
                    Import
                </Button>
            </GlobalTopBar.Actions>

            <AppPageLayout.Body className="min-h-0 flex-1 overflow-hidden">
                <PanelGroup direction="vertical">
                    <Panel
                        defaultSize={70}
                        minSize={20}
                    >
                        <div className="flex h-full w-full min-w-0">
                            <SectionPreviewPane
                                visible={previewMode}
                                iframeRef={iframeRef}
                                iframeURL={iframeURL}
                                previewError={previewError}
                                connectionError={connectionError}
                            />

                            <div
                                className={`w-full min-w-0 ${previewMode ? 'hidden' : ''}`}
                                aria-hidden={previewMode}
                            >
                                <TiptapEditor
                                    ref={tiptapEditorRef}
                                    content={jsonContent || 'Importing a section...'}
                                    isInitialContentLoaded={isInitialContentLoaded}
                                    onCreate={handleTipTapCreate}
                                    onUpdate={handleTiptapUpdate}
                                    useCollaboration={false}
                                />
                            </div>
                        </div>
                    </Panel>

                    <PanelResizeHandle className="group relative h-2 bg-gray-100 hover:bg-gray-200">
                        <div className="absolute top-1/2 left-1/2 h-1 w-16 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-400 group-hover:bg-gray-600" />
                    </PanelResizeHandle>

                    {isDevMode && (
                        <Panel minSize={0}>
                            <div className="grid h-full min-h-0 grid-cols-2 gap-6 bg-white p-4 shadow">
                                <div className="flex min-h-0 flex-col">
                                    <div className="mb-2 flex items-center gap-4">
                                        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-700">ProseMirror JSON</h2>
                                        <button
                                            className="cursor-pointer active:text-blue-500"
                                            onClick={() => setPMJsonUseMonaco(v => !v)}
                                        >
                                            <FileJsonIcon size={16} />
                                        </button>
                                        <button
                                            className="cursor-pointer active:text-blue-500"
                                            onClick={() => handleRefreshPMJson()}
                                        >
                                            <RefreshCwIcon size={14} />
                                        </button>
                                        <div className="ml-auto flex items-center gap-2">
                                            <span className="text-xs text-gray-500">Live update</span>
                                            <Switch
                                                checked={isLivePMJsonEnabled}
                                                onCheckedChange={setIsLivePMJsonEnabled}
                                            />
                                        </div>
                                    </div>
                                    <div className="h-full min-h-0 flex-1 rounded bg-gray-100 p-2 text-xs">
                                        {pmJsonUseMonaco ? (
                                            <MonacoEditor
                                                defaultLanguage="json"
                                                value={JSON.stringify(jsonContent, null, 2) || ''}
                                                options={editorOptions}
                                            />
                                        ) : (
                                            <pre className="h-full overflow-auto">{JSON.stringify(jsonContent, null, 2)}</pre>
                                        )}
                                    </div>
                                </div>
                                <div className="flex min-h-0 flex-col">
                                    <div className="mb-2 flex items-center gap-4">
                                        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-700">Output</h2>
                                        <div className="flex gap-2">
                                            <button
                                                className={`rounded px-2 py-1 text-xs ${outputType === 'html' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                                                onClick={() => setOutputType('html')}
                                            >
                                                HTML
                                            </button>
                                            <button
                                                className={`rounded px-2 py-1 text-xs ${outputType === 'json' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                                                onClick={() => setOutputType('json')}
                                            >
                                                JSON
                                            </button>
                                        </div>
                                        <button
                                            className="cursor-pointer active:text-blue-500"
                                            onClick={() => setOutputUseMonaco(v => !v)}
                                        >
                                            <FileJsonIcon size={16} />
                                        </button>
                                        <button
                                            className="cursor-pointer active:text-blue-500"
                                            onClick={() => handleCopyOutput()}
                                        >
                                            <Copy size={14} />
                                        </button>
                                        <button
                                            disabled={isRenderingOutput}
                                            className={`cursor-pointer ${isRenderingOutput ? 'animate-spin opacity-50' : ''}`}
                                            onClick={() => handleRefreshOutput()}
                                        >
                                            <RefreshCwIcon size={14} />
                                        </button>
                                        <div className="ml-auto flex items-center gap-2">
                                            <span className="text-xs text-gray-500">Live update</span>
                                            <Switch
                                                checked={isLiveOutputEnabled}
                                                onCheckedChange={setIsLiveOutputEnabled}
                                            />
                                        </div>
                                    </div>
                                    <div className="min-h-0 flex-1 overflow-auto rounded bg-gray-100 p-2 text-xs">
                                        {outputUseMonaco ? (
                                            <MonacoEditor
                                                defaultLanguage={renderedOutput.language}
                                                value={renderedOutput.value}
                                                options={editorOptions}
                                            />
                                        ) : (
                                            <pre className="h-full overflow-auto whitespace-pre-wrap">{renderedOutput.value}</pre>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Panel>
                    )}
                </PanelGroup>
            </AppPageLayout.Body>

            <Dialog
                open={showImportModal}
                onOpenChange={open => {
                    setShowImportModal(open);
                    if (!open) {
                        setImportModalError(null);
                    }
                }}
            >
                <DialogContent className="max-w-3xl sm:max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>Import</DialogTitle>
                    </DialogHeader>

                    <Tabs
                        defaultValue="paste"
                        className="w-full"
                    >
                        <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="paste">Paste content</TabsTrigger>
                            <TabsTrigger value="server">From server</TabsTrigger>
                        </TabsList>

                        <TabsContent
                            value="paste"
                            className="mt-4"
                        >
                            <div className="mb-2 flex gap-4">
                                <div className="flex flex-col">
                                    <label
                                        className="mb-1 text-sm text-gray-600"
                                        htmlFor="import-subject"
                                    >
                                        Subject
                                    </label>
                                    <Input
                                        type="text"
                                        className="rounded border border-gray-300 px-2 py-1 text-xs"
                                        placeholder="e.g. LinearAlgebra"
                                        value={importSubject}
                                        onChange={e => {
                                            setImportSubject(e.target.value);
                                            setImportDevBookName(`${e.target.value}Dev`);
                                        }}
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <label
                                        className="mb-1 text-sm text-gray-600"
                                        htmlFor="import-devbookname"
                                    >
                                        Devbook name
                                    </label>
                                    <Input
                                        type="text"
                                        className="rounded border border-gray-300 px-2 py-1 text-xs"
                                        placeholder="e.g. LinearAlgebraDev"
                                        value={importDevBookName}
                                        onChange={e => setImportDevBookName(e.target.value)}
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <label
                                        className="mb-1 text-sm text-gray-600"
                                        htmlFor="import-releaseid"
                                    >
                                        Release ID
                                    </label>
                                    <Input
                                        type="text"
                                        className="rounded border border-gray-300 px-2 py-1 text-xs"
                                        placeholder="e.g. 65"
                                        value={importReleaseId}
                                        onChange={e => setImportReleaseId(e.target.value)}
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <label
                                        className="mb-1 text-sm text-gray-600"
                                        htmlFor="import-sectionid"
                                    >
                                        Section ID
                                    </label>
                                    <Input
                                        type="text"
                                        className="rounded border border-gray-300 px-2 py-1 text-xs"
                                        placeholder="e.g. xxxx-xxxx-xxxx-xxxx"
                                        value={importSectionId}
                                        onChange={e => setImportSectionId(e.target.value)}
                                    />
                                </div>
                            </div>
                            <Textarea
                                value={importText}
                                onChange={e => setImportText(e.target.value)}
                                placeholder="Paste your content here..."
                                rows={10}
                            />
                            <div className="mt-2 text-sm text-gray-500">
                                <strong>Note (zyXML import only):</strong>
                                <ul className="mt-1 list-disc pl-5">
                                    <li>Only the first section from the zyXML will be imported.</li>
                                    <li>
                                        Please fill in <span className="font-semibold">subject</span> and{' '}
                                        <span className="font-semibold">release ID</span> above for the imported content to render
                                        correctly.
                                    </li>
                                </ul>
                                <p className="mt-2">
                                    <strong>Test bank import:</strong> paste a single test question object or an array of objects (see
                                    example.js format).
                                </p>
                            </div>
                            {importModalError && (
                                <p
                                    className="mt-2 text-sm text-destructive"
                                    role="alert"
                                >
                                    {importModalError}
                                </p>
                            )}
                            <DialogFooter className="mt-4 flex items-center sm:justify-end">
                                {isImportingContent && <LoaderCircleIcon className="animate-spin text-gray-300" />}
                                <Button
                                    disabled={isImportingContent}
                                    size="sm"
                                    onClick={() => handleImportZyxml()}
                                >
                                    Import zyXml
                                </Button>
                                <Button
                                    disabled={isImportingContent}
                                    size="sm"
                                    onClick={() => handleImport('html')}
                                >
                                    Import HTML
                                </Button>
                                <Button
                                    disabled={isImportingContent}
                                    size="sm"
                                    onClick={() => handleImport('json')}
                                >
                                    Import JSON
                                </Button>
                                <Button
                                    disabled={isImportingContent}
                                    size="sm"
                                    onClick={() => handleImportTestBank()}
                                >
                                    Import Test Bank
                                </Button>
                            </DialogFooter>
                        </TabsContent>

                        <TabsContent
                            value="server"
                            className="mt-4"
                        >
                            <div className="flex gap-4">
                                <div className="flex flex-1 flex-col">
                                    <label
                                        className="mb-1 text-sm text-gray-600"
                                        htmlFor="import-server-devbookname"
                                    >
                                        Devbook name
                                    </label>
                                    <Input
                                        id="import-server-devbookname"
                                        type="text"
                                        className="rounded border border-gray-300 px-2 py-1 text-xs"
                                        placeholder="e.g. LinearAlgebraDev"
                                        value={importServerDevBookName}
                                        onChange={e => {
                                            setImportServerDevBookName(e.target.value);
                                            setImportModalError(null);
                                        }}
                                    />
                                </div>
                                <div className="flex flex-1 flex-col">
                                    <label
                                        className="mb-1 text-sm text-gray-600"
                                        htmlFor="import-ma-document-id"
                                    >
                                        ma_document_id
                                    </label>
                                    <Input
                                        id="import-ma-document-id"
                                        type="text"
                                        className="rounded border border-gray-300 px-2 py-1 text-xs"
                                        placeholder="e.g. 42"
                                        value={importMaDocumentId}
                                        onChange={e => {
                                            setImportMaDocumentId(e.target.value);
                                            setImportModalError(null);
                                        }}
                                    />
                                </div>
                            </div>
                            <p className="mt-2 text-sm text-gray-500">
                                Loads TipTap JSON from the document server (no collaboration). Subject is resolved via the devbook code
                                (including lab dev books).
                            </p>
                            {importModalError && (
                                <p
                                    className="mt-2 text-sm text-destructive"
                                    role="alert"
                                >
                                    {importModalError}
                                </p>
                            )}
                            <DialogFooter className="mt-4 flex items-center sm:justify-end">
                                {isImportingContent && <LoaderCircleIcon className="animate-spin text-gray-300" />}
                                <Button
                                    disabled={isImportingContent}
                                    size="sm"
                                    onClick={() => handleImportMaDocument()}
                                >
                                    Import from server
                                </Button>
                            </DialogFooter>
                        </TabsContent>
                    </Tabs>
                </DialogContent>
            </Dialog>
        </AppPageLayout>
    );
};

export default EditorDev;
